import {
  isDefined,
  isNotEmpty,
  messageFromError,
  toResponseError,
  type InstallAddInput,
  type InstallAddSuccess,
  type InstallCountInput,
  type InstallCountSuccess,
  type InstallData,
  type InstallGetInput,
  type InstallGetSuccess,
  type InstallId,
  type InstallListInput,
  type InstallListSuccess,
  type InstallRemoveInput,
  type InstallRemoveSuccess,
  type InstallUpdateInput,
  type InstallUpdateSuccess,
} from '@terros/common'
import { useQueryClient } from '@tanstack/react-query'
import { useTantalimQuery } from '../query'
import { useTantalimMutation } from '../mutation'
import {
  mutationAdd,
  mutationUpdate,
  type AddHookResponse,
  type GetHookResponse,
  type RemoveHookResponse,
  type UpdateHookResponse,
} from '../hooks'
import { connectInstallCountKey, connectInstallKey, connectInstallListKey } from './keys'

export function useConnectInstalls(input?: InstallListInput): GetHookResponse<InstallData[]> {
  return useTantalimQuery<InstallListInput, InstallListSuccess, InstallData[]>({
    url: '/connect/install/list',
    queryKey: connectInstallListKey(input),
    input: input ?? {},
    convertResponse: ({ installs }) => installs.map(normalizeInstall),
  })
}

export function useConnectInstall(installId?: InstallId, enabled: boolean = true): GetHookResponse<InstallData> {
  const input: InstallGetInput | undefined = isDefined(installId) ? { installId } : undefined
  const isEnabled = isDefined(installId) && enabled

  return useTantalimQuery<InstallGetInput | undefined, InstallGetSuccess, InstallData>({
    url: '/connect/install/get',
    queryKey: connectInstallKey(installId),
    input,
    convertResponse: ({ install }) => normalizeInstall(install),
    options: {
      enabled: isEnabled,
    },
  })
}

export function useConnectInstallCount(input?: InstallCountInput): GetHookResponse<number> {
  return useTantalimQuery<InstallCountInput | undefined, InstallCountSuccess, number>({
    url: '/connect/install/count',
    queryKey: connectInstallCountKey(input?.appId),
    input,
    convertResponse: ({ count }) => count,
    options: {
      enabled: isDefined(input?.appId),
    },
  })
}

export function useAddConnectInstall(): AddHookResponse<InstallAddInput, InstallData> {
  const mutation = useTantalimMutation<InstallAddInput, InstallAddSuccess>({
    url: '/connect/install/add',
  })
  const queryClient = useQueryClient()

  async function add(input: InstallAddInput): Promise<InstallData> {
    try {
      const added = await mutation.mutateAsync(input)
      queryClient.setQueryData<InstallData>(connectInstallKey(added.install.installId), normalizeInstall(added.install))
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: connectInstallListKey() }),
        queryClient.invalidateQueries({ queryKey: connectInstallKey(added.install.installId) }),
        queryClient.invalidateQueries({ queryKey: connectInstallCountKey() }),
      ])
      return normalizeInstall(added.install)
    } catch (error) {
      throw toResponseError(error)
    }
  }

  return mutationAdd(mutation, add)
}

export function useUpdateConnectInstall(): UpdateHookResponse<InstallUpdateInput, InstallData> {
  const mutation = useTantalimMutation<InstallUpdateInput, InstallUpdateSuccess>({
    url: '/connect/install/update',
  })
  const queryClient = useQueryClient()

  async function update(input: InstallUpdateInput): Promise<InstallData> {
    try {
      const updated = await mutation.mutateAsync(input)
      queryClient.setQueryData<InstallData>(
        connectInstallKey(updated.install.installId),
        normalizeInstall(updated.install)
      )
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: connectInstallListKey() }),
        queryClient.invalidateQueries({ queryKey: connectInstallKey(updated.install.installId) }),
        queryClient.invalidateQueries({ queryKey: connectInstallCountKey() }),
      ])
      return normalizeInstall(updated.install)
    } catch (error) {
      throw toResponseError(error)
    }
  }

  return mutationUpdate(mutation, update)
}

export function useRemoveConnectInstall(): RemoveHookResponse<InstallRemoveInput> {
  const mutation = useTantalimMutation<InstallRemoveInput, InstallRemoveSuccess>({
    url: '/connect/install/remove',
  })
  const queryClient = useQueryClient()

  async function remove(input: InstallRemoveInput): Promise<void> {
    try {
      await mutation.mutateAsync(input)
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: connectInstallListKey() }),
        queryClient.invalidateQueries({ queryKey: connectInstallKey(input.installId) }),
        queryClient.invalidateQueries({ queryKey: connectInstallCountKey() }),
      ])
    } catch (error) {
      throw toResponseError(error)
    }
  }

  return {
    remove,
    isRemoving: mutation.isPending,
    error: messageFromError(mutation.error),
  }
}

function parseScriptTypes(value: string | string[], fallback: string[]): string[] {
  if (Array.isArray(value)) return value
  if (typeof value === 'string')
    return value
      .split(',')
      .map((v) => v.trim())
      .filter(isNotEmpty)
  return fallback
}

function normalizeInstall(install: InstallData): InstallData {
  const scriptTypes = parseScriptTypes(
    install.scriptTypes,
    install.scripts.map((s) => s.type)
  )
  return { ...install, scriptTypes }
}
